import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";
import { useAuth } from "./useAuth";

/**
 * @typedef {object} PartnershipPagination
 * @property {number} page
 * @property {number} rowsPerPage
 */

/**
 * @typedef {object} PartnershipFilter
 * @property {string} search
 */

/**
 * @typedef {object} PartnershipSorting
 * @property {string} orderBy
 * @property {string} order
 */

/**
 * @typedef {object} TargetAdminDetails
 * @property {number} id
 * @property {string} email
 * @property {string} first_name
 * @property {string} last_name
 * @property {date} last_login
 * @property {date} date_joined
 * @property {string} rut
 * @property {integer} country
 * @property {string} permission
 * @property {string} tms_token
 * @property {string} company_prefix
 * @property {Plan} plan
 * @property {boolean} allows_upselling
 * @property {integer} upselling_capacity
 */
/**
 * @typedef {object} SourceAdminDetails
 * @property {number} id
 * @property {string} email
 * @property {string} first_name
 * @property {string} last_name
 * @property {date} last_login
 * @property {date} date_joined
 * @property {string} rut
 * @property {integer} country
 * @property {string} permission
 * @property {string} tms_token
 * @property {string} company_prefix
 * @property {Plan} plan
 * @property {boolean} allows_upselling
 * @property {integer} upselling_capacity
 */

/**
 * @typedef {object} PartnershipData
 * @property {number} id
 * @property {number} source_admin
 * @property {number} target_admin
 * @property {TargetAdminDetails} target_admin_details
 * @property {SourceAdminDetails} source_admin_details
 * @property {number} base_cost
 * @property {boolean} is_active
 * @property {string} created_at
 */

/**
 * Initializes state from localStorage.
 * @returns {{filters: PartnershipFilter, pagination: PartnershipPagination, sorting: PartnershipSorting}}
 */
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("partnershipFilter");
  const defaults = {
    filters: {
      search: "",
    },
    pagination: {
      page: 1,
      rowsPerPage: 20,
    },
    sorting: {
      orderBy: "name",
      order: "asc",
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    try {
      const finalState = {
        filters: { ...defaults.filters, ...(savedState.filters || {}) },
        pagination: {
          ...defaults.pagination,
          ...(savedState.pagination || {}),
        },
        sorting: { ...defaults.sorting, ...(savedState.sorting || {}) },
      };
      return finalState;
    } catch (error) {
      console.error(
        `Error obteniendo los datos del localStorage "partnershipFilter": ${error}`
      );
      return defaults;
    }
  }
  return defaults;
};

/**
 * Custom Hook to manage DATA state for the Parnerships page
 *
 * Handles data fetching (GET), pagination, sorting, filtering,
 * and CRUD operations (POST, DELETE)
 * It does NOT manage UI state
 *
 * @returns {{
 * availablePartnerships: Array<TargetAdminDetails>,
 * partnerships: Array<PartnershipData>,
 * loading: boolean,
 * error: string | null,
 * pagination: object,
 * filters: object,
 * sorting: object,
 * partnershipsCount: number,
 * handleChangePage: (newPage: number) => void,
 * handleChangeRowsPerPage: (newRows: number) => void,
 * handleSortChange: (orderBy: string) => void,
 * createPartnership: (partnership: {target_admin: number, base_cost: number}) => Promise<void>,
 * deletePartnership: (partnershipId: number) => Promise<void>,
 * fetchAvailablePartnerships: () => Promise<void>
 * refetchPartnerships: () => Promise<void>
 * assignDeliveries: (orderIds: Array<number>, partnershipId: number) => Promise<void>
 * }}
 */

export function usePartnerships() {
  // Data status and IU
  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [sorting, setSorting] = useState(initialState.sorting);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [auxSearcher, setAuxSearcher] = useState(filters.search);
  const [availablePartnerships, setAvailablePartnerships] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [partnershipsCount, setPartnershipsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    user: { admin },
  } = useAuth();
  // STATE MANNERS (HANDLERS)
  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleSearchChange = useCallback((value) => {
    setAuxSearcher(value);
  }, []);

  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const handleSortChange = useCallback((orderBy) => {
    setSorting((previous) => ({
      orderBy: orderBy,
      order:
        previous.orderBy === orderBy && previous.order === "asc"
          ? "desc"
          : "asc",
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const fetchAvailablePartnerships = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { partners },
      } = await axios.get("upsellings/partnerships/availables/");
      setAvailablePartnerships(partners || []);
    } catch (error) {
      console.error(`No se pudo obtener los partners disponibles: ${error}`);
      setError("No se pudo obtener los partners disponibles");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPartnerships = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      page: pagination.page,
      page_size: pagination.rowsPerPage,
    };
    try {
      const {
        data: { results: partnerships, count },
      } = await axios.get(`upsellings/partnerships/list/${admin.id}/`, {
        params,
      });
      setPartnerships(partnerships || []);
      setPartnershipsCount(count || 0);
    } catch (error) {
      console.error(`No se pudo obtener las alianzas: ${error}`);
      setError("No se pudo obtener las alianzas");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, sorting]);

  const createPartnership = useCallback(
    async (partnership) => {
      setLoading(true);
      setError(null);
      try {
        await axios.post("upsellings/partnerships/create/", partnership);
        fetchPartnerships();
      } catch (error) {
        console.error(`Error al crear una nueva alianza: ${error}`);
        setError("Error al crear una nueva alianza");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchPartnerships]
  );

  const deletePartnership = useCallback(
    async (partnershipId) => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(`upsellings/partnerships/delete/${partnershipId}`);
        fetchPartnerships();
      } catch (error) {
        console.error(`Error al intentar eliminar la alianza: ${error}`);
        setError("Error al intentar eliminar la alianza");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchPartnerships]
  );

  const assignDeliveries = useCallback(async ({ orderIds, partnershipId }) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        "upsellings/partnerships/assign/",
        {
          order_ids: orderIds,
          partnership_id: partnershipId,
        }
      );
      return data;
    } catch (error) {
      console.error(`No se pudo assinar los envíos: ${error}`);
      setError("No se pudo assinar los envíos");
      if ([400, 404].includes(error.status)) return error?.response?.data;
    } finally {
      setLoading(false);
    }
  }, []);

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (auxSearcher !== filters.search) {
        handleFilterChange("search", auxSearcher);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [auxSearcher, filters.search, handleFilterChange]);

  useEffect(() => {
    localStorage.setItem(
      "partnershipFilter",
      JSON.stringify({
        ...filters,
        ...pagination,
      })
    );
  }, [filters, pagination]);

  useEffect(() => {
    fetchPartnerships();
    fetchAvailablePartnerships();
  }, [fetchPartnerships, fetchAvailablePartnerships]);

  // PUBLIC API OF THE HOOK
  return {
    // Data
    availablePartnerships,
    partnerships,
    partnershipsCount,

    // UI State
    loading,
    error,

    // Control State
    pagination,
    filters: { ...filters, search: auxSearcher },
    sorting,

    // Actions
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
    handleSearchChange,

    // API Calls
    refetchAvailablePartnerships: fetchAvailablePartnerships,
    createPartnership,
    deletePartnership,
    refetchPartnerships: fetchPartnerships,
    assignDeliveries,
  };
}
